// The view: builds the Arcade DOM and exposes imperative render methods + event hooks. All
// ceremony logic lives in state.ts — this file only draws. Palette is brick/green/gold on
// stone (styles.css); there is deliberately no purple anywhere.

import { candleLine, HOUSE_LINE, pityLine, resultCopy, VERIFY_FAIL_LINE, VERIFY_PASS_LINE } from './copy';
import { renderSigil } from './sigil';
import { Wheel } from './wheel';
import type { WheelEntry } from './wheel';
import type { Mode, Settlement } from './types';
import type { VerifyResult } from './fairness';

export class ArcadeUI {
  private root: HTMLElement;
  private els!: {
    fs: HTMLElement;
    tickets: HTMLElement;
    paidCap: HTMLElement;
    candle: HTMLElement;
    sigilBox: HTMLElement;
    commitText: HTMLElement;
    canvas: HTMLCanvasElement;
    status: HTMLElement;
    spinBtn: HTMLButtonElement;
    paidToggle: HTMLButtonElement;
    freeToggle: HTMLButtonElement;
    resultCard: HTMLElement;
    retryBtn: HTMLButtonElement;
    verifyBtn: HTMLButtonElement;
    verifyPanel: HTMLElement;
    pity: HTMLElement;
    muteBtn: HTMLButtonElement;
  };
  private mode: Mode = 'paid';
  private lastCommit = '';

  constructor(root: HTMLElement) {
    this.root = root;
    this.build();
  }

  private build(): void {
    this.root.innerHTML = `
      <div class="arcade">
        <header class="topbar">
          <div class="stat"><span class="k">FlaxScript</span><span class="v" data-fs>—</span></div>
          <div class="stat"><span class="k">Spin Tickets</span><span class="v" data-tickets>—</span></div>
          <div class="stat"><span class="k">Today</span><span class="v" data-paidcap>—/—</span></div>
          <div class="stat candle"><span class="k">The Candle</span><span class="v" data-candle>—</span></div>
        </header>

        <main class="stage">
          <div class="seal">
            <div class="sigil" data-sigil></div>
            <div class="seal-meta">
              <div class="seal-title">The seal</div>
              <code class="commit" data-commit>awaiting the altar…</code>
            </div>
          </div>
          <canvas class="wheel" data-canvas aria-label="the wheel"></canvas>
          <p class="pity" data-pity></p>
          <p class="status" data-status></p>
        </main>

        <section class="controls">
          <div class="modes" role="tablist" aria-label="spin mode">
            <button class="mode active" data-paid role="tab">Offer 50 FS</button>
            <button class="mode" data-free role="tab">Sunday ticket</button>
            <button class="mute-btn" data-mute aria-label="toggle sound" title="sound">🔊</button>
          </div>
          <button class="spin" data-spin disabled>Spin</button>
          <button class="retry hidden" data-retry>Retry the same spin</button>
        </section>

        <section class="result hidden" data-result></section>

        <section class="verify">
          <button class="verify-btn" data-verify>🔍 Verify — rotate &amp; reveal my seed</button>
          <div class="verify-panel" data-verifypanel></div>
        </section>

        <footer class="house">
          <p>${HOUSE_LINE}</p>
          <a class="odds" href="#" data-odds>/wheel_odds — the table is published</a>
        </footer>
      </div>`;

    const q = <T extends Element>(sel: string) => this.root.querySelector(sel) as T;
    this.els = {
      fs: q('[data-fs]'),
      tickets: q('[data-tickets]'),
      paidCap: q('[data-paidcap]'),
      candle: q('[data-candle]'),
      sigilBox: q('[data-sigil]'),
      commitText: q('[data-commit]'),
      canvas: q('[data-canvas]'),
      status: q('[data-status]'),
      spinBtn: q('[data-spin]'),
      paidToggle: q('[data-paid]'),
      freeToggle: q('[data-free]'),
      resultCard: q('[data-result]'),
      retryBtn: q('[data-retry]'),
      verifyBtn: q('[data-verify]'),
      verifyPanel: q('[data-verifypanel]'),
      pity: q('[data-pity]'),
      muteBtn: q('[data-mute]'),
    };

    this.els.paidToggle.addEventListener('click', () => this.setMode('paid'));
    this.els.freeToggle.addEventListener('click', () => this.setMode('free'));
  }

  mountWheel(entries: WheelEntry[]): Wheel {
    return new Wheel(this.els.canvas, entries);
  }

  // --- top bar ---
  renderTopBar(v: {
    fs: number;
    ticketsText: string;
    paidToday: number;
    cap: number;
    candleText: string;
    commitHash: string;
  }): void {
    this.els.fs.textContent = String(v.fs);
    this.els.tickets.textContent = v.ticketsText;
    this.els.paidCap.textContent = `${v.paidToday}/${v.cap}`;
    this.els.candle.textContent = v.candleText;
    this.showSeal(v.commitHash);
  }

  // The pity register — a monospace ledger line, deliberately not a filling bar.
  renderPity(count: number, threshold: number): void {
    this.els.pity.textContent = pityLine(count, threshold);
  }

  showSeal(commitHash: string): void {
    if (!commitHash) return;
    this.lastCommit = commitHash;
    this.els.commitText.textContent = commitHash;
    this.els.sigilBox.replaceChildren(renderSigil(commitHash, { size: 84 }));
  }

  crackSigil(): void {
    if (!this.lastCommit) return;
    this.els.sigilBox.replaceChildren(renderSigil(this.lastCommit, { size: 84, cracked: true }));
  }

  // --- mode ---
  getMode(): Mode {
    return this.mode;
  }

  private setMode(mode: Mode): void {
    this.mode = mode;
    this.els.paidToggle.classList.toggle('active', mode === 'paid');
    this.els.freeToggle.classList.toggle('active', mode === 'free');
    this.modeChangeCb?.(mode);
  }

  // --- controls ---
  setSpinEnabled(enabled: boolean, label?: string): void {
    this.els.spinBtn.disabled = !enabled;
    if (label) this.els.spinBtn.textContent = label;
  }

  // Mode tabs lock while a spin is in flight — a mid-spin relayout would let the pointer
  // land on a wedge that is not the settled tier (see wheel.ts setLayout).
  setModeEnabled(enabled: boolean): void {
    this.els.paidToggle.disabled = !enabled;
    this.els.freeToggle.disabled = !enabled;
  }

  setStatus(text: string): void {
    this.els.status.textContent = text;
  }

  showRetry(show: boolean): void {
    this.els.retryBtn.classList.toggle('hidden', !show);
  }

  // --- result ---
  // The honest reveal: headline = NET balance movement (never the gross prize — the LDW
  // ban), balance before → after, candle line on the paid offering when the pot is known.
  // Net comes from the settlement's own ledger leg (deltas.user), NOT from a cached client
  // balance — FS earned outside the Activity between refreshes must never be credited to
  // (or debited from) the spin's headline.
  revealOutcome(
    settlement: Settlement,
    mode: Mode,
    candle?: { potBefore: number; rakeFs: number },
  ): void {
    const { prize_tier, prize_fs, jackpot_win } = settlement.outcome;
    const net = mode === 'paid' ? (settlement.deltas['user'] ?? 0) : 0;
    const balanceBefore = settlement.new_balance - net;
    const copy = resultCopy(prize_tier, net, prize_fs, jackpot_win, mode);

    const card = this.els.resultCard;
    card.classList.remove('hidden', 'reveal');
    card.dataset.tier = prize_tier;
    card.dataset.tone = copy.tone;
    const pieces: string[] = [];
    if (copy.big) pieces.push(`<p class="big" data-big></p>`);
    pieces.push(`<p class="line">${copy.sub}</p>`);
    if (mode === 'paid') {
      pieces.push(`<p class="move">${balanceBefore} → ${settlement.new_balance} FS</p>`);
      if (candle && prize_tier === 'nothing') {
        pieces.push(`<p class="move">${candleLine(candle.potBefore, candle.rakeFs)}</p>`);
      }
    }
    card.innerHTML = pieces.join('');
    // Re-trigger the entry animation even on back-to-back reveals.
    void card.offsetWidth;
    card.classList.add('reveal');

    const bigEl = card.querySelector<HTMLElement>('[data-big]');
    if (bigEl) {
      if (copy.big.endsWith(' FS') && copy.big !== '±0 FS') {
        this.tweenNumber(bigEl, net, ' FS');
      } else {
        bigEl.textContent = copy.big;
      }
    }
  }

  clearResult(): void {
    this.els.resultCard.classList.add('hidden');
    this.els.resultCard.innerHTML = '';
  }

  // Count-up for an FS amount (the sign and unit stay fixed; the magnitude rolls).
  // signed=false renders the bare magnitude — for amounts that are not a net claim.
  private tweenNumber(
    el: HTMLElement,
    to: number,
    suffix: string,
    durationMs = 600,
    signed = true,
  ): void {
    const sign = !signed ? '' : to > 0 ? '+' : to < 0 ? '−' : '';
    const target = Math.abs(to);
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = `${sign}${Math.round(target * eased)}${suffix}`;
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  tweenBalance(to: number, durationMs = 700): void {
    const fromText = this.els.fs.textContent ?? '0';
    const from = Number(fromText.replace(/[^\d-]/g, '')) || 0;
    if (from === to) {
      this.els.fs.textContent = String(to);
      return;
    }
    const stat = this.els.fs.closest('.stat');
    if (stat) {
      stat.classList.remove('pulse');
      void (stat as HTMLElement).offsetWidth;
      stat.classList.add('pulse');
      window.setTimeout(() => stat.classList.remove('pulse'), 600);
    }
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      this.els.fs.textContent = String(Math.round(from + (to - from) * eased));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  // The one full-screen moment: the candle inverts and drains toward the player.
  jackpotMoment(totalFs: number, candleFrom?: number): void {
    const overlay = document.createElement('div');
    overlay.className = 'overlay jackpot-overlay';
    overlay.innerHTML = `
      <p class="overlay-kicker">the pot broke</p>
      <p class="overlay-big" data-total>0 FS</p>
      <p class="overlay-sub">${candleFrom != null ? `the candle drains — ${candleFrom} → 0` : 'the Void resets the candle'}</p>`;
    document.body.appendChild(overlay);
    const total = overlay.querySelector<HTMLElement>('[data-total]');
    // Unsigned: this is the pot amount, not a net claim — the signed headline lives on
    // the result card only ("the pot broke" + drain line carry the meaning here).
    if (total) this.tweenNumber(total, totalFs, ' FS', 1200, false);
    const dismiss = () => overlay.remove();
    overlay.addEventListener('click', dismiss);
    window.setTimeout(dismiss, 4000);
  }

  // Golden ticket: everything goes silent — black stage, the apple, one line of white text.
  goldenMoment(line: string): void {
    const overlay = document.createElement('div');
    overlay.className = 'overlay golden-overlay';
    overlay.innerHTML = `
      <p class="overlay-apple">🍎</p>
      <p class="overlay-line">${line}</p>`;
    document.body.appendChild(overlay);
    const dismiss = () => overlay.remove();
    overlay.addEventListener('click', dismiss);
    window.setTimeout(dismiss, 4500);
  }

  // --- audio toggle ---
  setMuteState(muted: boolean): void {
    this.els.muteBtn.textContent = muted ? '🔇' : '🔊';
    this.els.muteBtn.setAttribute('aria-pressed', String(muted));
  }

  onMute(cb: () => void): void {
    this.els.muteBtn.addEventListener('click', cb);
  }

  // --- verify ---
  renderVerify(results: VerifyResult[], revealedEpoch: number): void {
    const allPass = results.length > 0 && results.every((r) => r.pass);
    const header = document.createElement('div');
    header.className = allPass ? 'verify-head pass' : 'verify-head fail';
    if (results.length === 0) {
      header.textContent = `No recorded spins in epoch ${revealedEpoch} to recompute.`;
      header.className = 'verify-head';
    } else {
      header.textContent = allPass ? VERIFY_PASS_LINE : VERIFY_FAIL_LINE;
    }
    const list = document.createElement('ul');
    list.className = 'verify-list';
    for (const r of results) {
      const li = document.createElement('li');
      li.className = r.pass ? 'ok' : 'bad';
      li.textContent = `epoch ${revealedEpoch} · nonce ${r.nonce} — ${r.pass ? 'verified' : r.reason}`;
      list.appendChild(li);
    }
    this.els.verifyPanel.replaceChildren(header, list);
    if (allPass) this.crackSigil();
  }

  setVerifyBusy(busy: boolean, label?: string): void {
    this.els.verifyBtn.disabled = busy;
    if (label) this.els.verifyBtn.textContent = label;
  }

  // --- event hooks ---
  private modeChangeCb?: (mode: Mode) => void;
  onSpin(cb: () => void): void {
    this.els.spinBtn.addEventListener('click', cb);
  }
  onRetry(cb: () => void): void {
    this.els.retryBtn.addEventListener('click', cb);
  }
  onVerify(cb: () => void): void {
    this.els.verifyBtn.addEventListener('click', cb);
  }
  onModeChange(cb: (mode: Mode) => void): void {
    this.modeChangeCb = cb;
  }
  onOdds(cb: () => void): void {
    this.root.querySelector('[data-odds]')?.addEventListener('click', (e) => {
      e.preventDefault();
      cb();
    });
  }

  fatal(message: string): void {
    this.root.innerHTML = `<div class="fatal"><p>${message}</p></div>`;
  }
}
